import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// ── Date helpers ──────────────────────────────────────────────────────────────

export function formatDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val.endsWith('Z') ? val : val + 'Z');
    if (isNaN(d)) return val;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return val; }
}

export function toDateStr(val) {
  if (!val) return '';
  return String(val).split('T')[0].split(' ')[0];
}

// ── OS badge ─────────────────────────────────────────────────────────────────

const OS_BADGE = {
  Windows: 'bg-blue-900/40 text-blue-300 border-blue-700/50',
  MacOS:   'bg-sky-900/40 text-sky-300 border-sky-700/50',
  iOS:     'bg-purple-900/40 text-purple-300 border-purple-700/50',
  Android: 'bg-purple-900/40 text-purple-300 border-purple-700/50',
  Linux:   'bg-green-900/40 text-green-300 border-green-700/50',
};

export function OSBadge({ value }) {
  const cls = OS_BADGE[value] || 'bg-gray-800/60 text-gray-400 border-gray-700/50';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded border ${cls}`}>
      {value || 'Unknown'}
    </span>
  );
}

export function BoolCell({ value }) {
  return value
    ? <span className="inline-flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-green-400" /></span>
    : <span className="inline-flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-gray-600" /></span>;
}

// ── Sort helper ───────────────────────────────────────────────────────────────

export function compareVals(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return dir === 'asc' ? -1 : 1;
  if (b == null) return dir === 'asc' ? 1 : -1;
  if (typeof a === 'boolean') {
    if (a === b) return 0;
    return dir === 'asc' ? (a ? -1 : 1) : (a ? 1 : -1);
  }
  const c = String(a).toLowerCase().localeCompare(String(b).toLowerCase(), undefined, { numeric: true });
  return dir === 'asc' ? c : -c;
}

// ── Column filter application ─────────────────────────────────────────────────

export function applyColFilters(rows, cols, filters) {
  return rows.filter((d) => {
    for (const col of cols) {
      const fv = filters[col.key];
      if (fv === '' || fv == null) continue;
      const cellVal = d[col.key];
      if (col.filterType === 'text') {
        if (!String(cellVal || '').toLowerCase().includes(fv.toLowerCase())) return false;
      } else if (col.filterType === 'select') {
        if (String(cellVal || '') !== fv) return false;
      } else if (col.filterType === 'multi-select') {
        // fv is a comma-separated list of selected values. Match is case-insensitive AND
        // prefix-aware: selecting "Windows" matches "Windows", "Windows 10", "Windows 11", etc.
        // Picking a more specific value like "Windows 11" still matches it exactly. Same idea
        // for "macOS Sonoma" / "macOS" etc.
        const selected = fv.split(',').filter(Boolean);
        if (selected.length === 0) continue;
        const cvLower = String(cellVal || '').toLowerCase();
        const hit = selected.some((s) => {
          const sLower = s.toLowerCase();
          return cvLower === sLower || cvLower.startsWith(sLower + ' ');
        });
        if (!hit) return false;
      } else if (col.filterType === 'bool') {
        // null is reserved for "not applicable" (e.g. DRMM/BPC on iOS/Android). Exclude N/A
        // rows from both "yes" and "no" filters — they're neither.
        if (cellVal === null) return false;
        const boolCell = cellVal === true || String(cellVal) === 'true';
        if (fv === 'yes' && !boolCell) return false;
        if (fv === 'no' && boolCell) return false;
      } else if (col.filterType === 'date-op') {
        const ds = toDateStr(cellVal);
        if (!ds) return false;
        const op = filters[`${col.key}_op`] || '>=';
        if (op === '>=' && ds < fv) return false;
        if (op === '<=' && ds > fv) return false;
      } else if (col.filterType === 'date') {
        if (String(cellVal || '') !== fv) return false;
      }
    }
    return true;
  });
}

export function countActiveFilters(filters) {
  return Object.entries(filters).filter(([k, v]) => !k.endsWith('_op') && v !== '').length;
}

// ── Sort indicator ────────────────────────────────────────────────────────────

export function SortIndicator({ active, dir }) {
  if (!active) return (
    <span className="ml-0.5 inline-flex flex-col opacity-20 align-middle">
      <ChevronUpIcon className="h-2.5 w-2.5 -mb-0.5" />
      <ChevronDownIcon className="h-2.5 w-2.5" />
    </span>
  );
  return dir === 'asc'
    ? <ChevronUpIcon className="ml-0.5 h-3 w-3 text-blue-400 inline align-middle" />
    : <ChevronDownIcon className="ml-0.5 h-3 w-3 text-blue-400 inline align-middle" />;
}

// ── Filter cell ───────────────────────────────────────────────────────────────

const INPUT_CLS = 'w-full bg-gray-800/80 border border-gray-700/80 rounded text-[11px] py-0.5 px-1.5 text-gray-300 placeholder-gray-600 focus:outline-none focus:border-blue-500';
const SELECT_CLS = INPUT_CLS + ' cursor-pointer';

function MultiSelectFilter({ col, filters, onFilterChange, distinct }) {
  const fv = filters[col.key] ?? '';
  const selected = fv ? fv.split(',').filter(Boolean) : [];
  const options = distinct[col.key] || [];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val];
    onFilterChange(col.key, next.join(','));
  };

  const label = selected.length === 0 ? 'All'
              : selected.length === 1 ? selected[0]
              : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={SELECT_CLS + ' text-left flex items-center justify-between'}
        title={selected.length > 1 ? selected.join(', ') : undefined}
      >
        <span className={`truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-300'}`}>{label}</span>
        <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0 ml-1" />
      </button>
      {open && (
        <div className="absolute z-20 mt-0.5 left-0 min-w-full max-w-[200px] max-h-56 overflow-y-auto bg-gray-900 border border-gray-700 rounded shadow-lg py-1">
          {options.length === 0 ? (
            <div className="px-2 py-1 text-[11px] text-gray-500">No values</div>
          ) : options.map((v) => (
            <label key={v} className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(v)}
                onChange={() => toggle(v)}
                className="h-3 w-3 accent-blue-500"
              />
              <span className="truncate">{v}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <div className="border-t border-gray-800 mt-1 pt-1">
              <button
                type="button"
                onClick={() => onFilterChange(col.key, '')}
                className="w-full text-left px-2 py-1 text-[11px] text-blue-400 hover:bg-gray-800"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FilterCell({ col, filters, onFilterChange, distinct }) {
  const fv  = filters[col.key] ?? '';
  const fop = filters[`${col.key}_op`] ?? '>=';

  switch (col.filterType) {
    case 'text':
      return <input type="text" value={fv} onChange={(e) => onFilterChange(col.key, e.target.value)} placeholder="Filter…" className={INPUT_CLS} />;
    case 'select':
      return (
        <select value={fv} onChange={(e) => onFilterChange(col.key, e.target.value)} className={SELECT_CLS}>
          <option value="">All</option>
          {(distinct[col.key] || []).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    case 'multi-select':
      return <MultiSelectFilter col={col} filters={filters} onFilterChange={onFilterChange} distinct={distinct} />;
    case 'bool':
      return (
        <select value={fv} onChange={(e) => onFilterChange(col.key, e.target.value)} className={SELECT_CLS}>
          <option value="">All</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    case 'date-op':
      return (
        <div className="flex gap-0.5">
          <select value={fop} onChange={(e) => onFilterChange(`${col.key}_op`, e.target.value)}
            className="bg-gray-800/80 border border-gray-700/80 rounded text-[11px] py-0.5 px-0.5 text-blue-400 focus:outline-none focus:border-blue-500 cursor-pointer flex-shrink-0 w-9"
            title="on or after / on or before">
            <option value=">=">≥</option>
            <option value="<=">≤</option>
          </select>
          <input type="date" value={fv} onChange={(e) => onFilterChange(col.key, e.target.value)} className={INPUT_CLS + ' flex-1 min-w-0'} />
        </div>
      );
    case 'date':
      return <input type="date" value={fv} onChange={(e) => onFilterChange(col.key, e.target.value)} className={INPUT_CLS} />;
    default:
      return null;
  }
}

// ── Resize hook ───────────────────────────────────────────────────────────────

export function useResizableCols(defaults) {
  const [colWidths, setColWidths] = useState(defaults);
  const thRefs   = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, idx) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = thRefs.current[idx]?.offsetWidth ?? colWidths[idx];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (mv) => {
      setColWidths((p) => { const n = [...p]; n[idx] = Math.max(50, startW + mv.clientX - startX); return n; });
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths]);

  return { colWidths, thRefs, tableRef, startResize };
}

// ── Table header + filter row ────────────────────────────────────────────────

export function TableHead({ cols, colWidths, thRefs, startResize, sortField, sortDir, onSort, filters, onFilterChange, distinct }) {
  return (
    <thead>
      <tr className="border-b border-gray-800 bg-gray-900/60">
        {cols.map((col, i) => (
          <th
            key={col.key}
            ref={(el) => { thRefs.current[i] = el; }}
            style={{ width: `${colWidths[i]}px` }}
            className={`px-3 py-2 font-medium text-gray-400 whitespace-nowrap relative select-none cursor-pointer ${col.align === 'center' ? 'text-center' : 'text-left'}`}
            onClick={() => onSort(col.key)}
          >
            <span className="text-xs">{col.label}</span>
            <SortIndicator active={sortField === col.key} dir={sortDir} />
            <div
              className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
              onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
            >
              <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
            </div>
          </th>
        ))}
      </tr>
      <tr className="border-b border-gray-800 bg-gray-900/30">
        {cols.map((col, i) => (
          <td key={col.key} style={{ width: `${colWidths[i]}px` }} className="px-1.5 py-1">
            <FilterCell col={col} filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
          </td>
        ))}
      </tr>
    </thead>
  );
}
